import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  BusinessCategoryAssignmentRecord,
  BusinessCategoryOption,
} from '@/lib/business-taxonomy';
import {
  normalizeBusinessPlanUsage,
  type BusinessPlanUsage,
  type BusinessPlanUsageRow,
} from '@/lib/plan-limits';

interface BusinessCategoryRow {
  description: string | null;
  icon: string | null;
  id: string;
  is_active: boolean;
  name: string;
  parent_id: string | null;
  requires_custom_text: boolean | null;
  search_aliases: string[] | null;
  slug: string;
  sort_order: number | null;
}

interface BusinessCategoryAssignmentRow {
  business_id: string;
  category_id: string;
  created_at: string;
  esta_activo: boolean;
  id: string;
  is_primary: boolean;
  oculto_por_plan: boolean;
  other_business_type_text: string | null;
}

export const BUSINESS_RULE_ERROR_CODES = [
  'OYB_BUSINESS_ACCESS_DENIED',
  'OYB_CATEGORY_LIMIT_REACHED',
  'OYB_SERVICE_LIMIT_REACHED',
  'OYB_CATEGORY_NOT_FOUND',
  'OYB_CATEGORY_CUSTOM_TEXT_REQUIRED',
  'OYB_ASSIGNMENT_NOT_FOUND',
  'OYB_PRIMARY_CATEGORY_REQUIRED',
] as const;

export type BusinessRuleErrorCode =
  (typeof BUSINESS_RULE_ERROR_CODES)[number];

function mapAssignmentRow(
  row: BusinessCategoryAssignmentRow
): BusinessCategoryAssignmentRecord {
  return {
    assignmentId: row.id,
    businessId: row.business_id,
    createdAt: row.created_at,
    hiddenByPlan: row.oculto_por_plan,
    isActive: row.esta_activo,
    isPrimary: row.is_primary,
    otherBusinessTypeText: row.other_business_type_text,
    typeId: row.category_id,
  };
}

export function getBusinessRuleErrorCode(
  error: unknown
): BusinessRuleErrorCode | null {
  if (!error) return null;

  const record =
    typeof error === 'object' ? (error as Record<string, unknown>) : null;
  const searchable = [
    typeof error === 'string' ? error : '',
    error instanceof Error ? error.message : '',
    typeof record?.code === 'string' ? record.code : '',
    typeof record?.message === 'string' ? record.message : '',
    typeof record?.details === 'string' ? record.details : '',
    typeof record?.hint === 'string' ? record.hint : '',
  ].join(' ');

  return (
    BUSINESS_RULE_ERROR_CODES.find((code) => searchable.includes(code)) ?? null
  );
}

export async function fetchActiveBusinessTaxonomy(
  client: SupabaseClient
): Promise<BusinessCategoryOption[]> {
  const response = await client
    .schema('oyb')
    .from('business_categories')
    .select(
      'id, name, slug, parent_id, icon, description, search_aliases, sort_order, is_active, requires_custom_text'
    )
    .eq('is_active', true)
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true })
    .returns<BusinessCategoryRow[]>();

  if (response.error) throw response.error;

  const rows = response.data ?? [];
  const childrenByParent = new Map<string, BusinessCategoryRow[]>();

  rows.forEach((row) => {
    if (!row.parent_id) return;
    const children = childrenByParent.get(row.parent_id) ?? [];
    children.push(row);
    childrenByParent.set(row.parent_id, children);
  });

  return rows
    .filter((row) => row.parent_id === null)
    .map((row) => ({
      description: row.description?.trim() || null,
      icon: row.icon?.trim() || null,
      id: row.id,
      name: row.name.trim(),
      searchAliases: (row.search_aliases ?? [])
        .map((alias) => alias.trim())
        .filter(Boolean),
      slug: row.slug.trim(),
      types: (childrenByParent.get(row.id) ?? []).map((type) => ({
        description: type.description?.trim() || null,
        icon: type.icon?.trim() || null,
        id: type.id,
        name: type.name.trim(),
        requiresCustomText: type.requires_custom_text === true,
        searchAliases: (type.search_aliases ?? [])
          .map((alias) => alias.trim())
          .filter(Boolean),
        slug: type.slug.trim(),
      })),
    }));
}

export async function fetchBusinessCategoryAssignments(
  client: SupabaseClient,
  businessId: string
): Promise<BusinessCategoryAssignmentRecord[]> {
  const response = await client
    .schema('oyb')
    .from('business_category_assignments')
    .select(
      'id, business_id, category_id, is_primary, other_business_type_text, created_at'
        + ', esta_activo, oculto_por_plan'
    )
    .eq('business_id', businessId)
    .order('is_primary', { ascending: false })
    .order('created_at', { ascending: true })
    .returns<BusinessCategoryAssignmentRow[]>();

  if (response.error) throw response.error;
  return (response.data ?? []).map(mapAssignmentRow);
}

export async function getBusinessPlanUsage(
  client: SupabaseClient,
  businessId: string
): Promise<BusinessPlanUsage> {
  const response = await client
    .schema('oyb')
    .rpc('get_business_plan_usage', { p_business_id: businessId })
    .returns<BusinessPlanUsageRow[]>();

  if (response.error) throw response.error;
  const rows = response.data as unknown as BusinessPlanUsageRow[] | null;
  const row = rows?.[0];
  if (!row) throw new Error('No se pudo consultar el uso del plan.');
  return normalizeBusinessPlanUsage(row);
}

export async function addBusinessCategoryAssignment(
  client: SupabaseClient,
  businessId: string,
  categoryId: string,
  otherBusinessTypeText: string | null,
  pendingByPlan = false
): Promise<BusinessCategoryAssignmentRecord> {
  if (pendingByPlan) {
    const pendingResponse = await client
      .schema('oyb')
      .from('business_category_assignments')
      .insert({
        business_id: businessId,
        category_id: categoryId,
        esta_activo: false,
        is_primary: false,
        oculto_por_plan: true,
        other_business_type_text: otherBusinessTypeText,
      })
      .select(
        'id, business_id, category_id, is_primary, other_business_type_text, created_at, esta_activo, oculto_por_plan'
      )
      .single<BusinessCategoryAssignmentRow>();

    if (pendingResponse.error) throw pendingResponse.error;
    return mapAssignmentRow(pendingResponse.data);
  }

  const response = await client
    .schema('oyb')
    .rpc('add_business_category_assignment', {
      p_business_id: businessId,
      p_category_id: categoryId,
      p_other_business_type_text: otherBusinessTypeText,
    })
    .single<BusinessCategoryAssignmentRow>();

  if (response.error) throw response.error;
  return mapAssignmentRow(response.data);
}

export async function setPrimaryBusinessCategory(
  client: SupabaseClient,
  businessId: string,
  categoryId: string,
  otherBusinessTypeText: string | null
): Promise<BusinessCategoryAssignmentRecord> {
  const response = await client
    .schema('oyb')
    .rpc('set_primary_business_category', {
      p_business_id: businessId,
      p_category_id: categoryId,
      p_other_business_type_text: otherBusinessTypeText,
    })
    .single<BusinessCategoryAssignmentRow>();

  if (response.error) throw response.error;
  return mapAssignmentRow(response.data);
}

export async function removeBusinessCategoryAssignment(
  client: SupabaseClient,
  businessId: string,
  assignmentId: string
): Promise<BusinessCategoryAssignmentRecord> {
  const response = await client
    .schema('oyb')
    .rpc('remove_business_category_assignment', {
      p_assignment_id: assignmentId,
      p_business_id: businessId,
    })
    .single<BusinessCategoryAssignmentRow>();

  if (response.error) throw response.error;
  return mapAssignmentRow(response.data);
}
